import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'clsx'

// Actions
import { actions as audioActions } from 'modules/audio/actions'

// Components
import { IconPrevious, IconNext, IconLoopSmall, IconLoopLoadingSmall } from '@tracklib/kit'
import PlayButton from 'components/playbutton'
import LoopButton from 'components/player/loopbutton'
import DownloadButton from 'components/base/downloadbutton'

// Hooks
import { useLoop } from 'modules/audio/hooks'

// Types
import { AudioState, State, Loop } from 'types'

import styles from './controls.css'

type ControlsProps = {
  trackId: number
  playButtonClassName?: string
  className?: string
  expanded?: boolean
}

const Controls = ({ trackId, playButtonClassName, className, expanded }: ControlsProps): JSX.Element => {
  const dispatch = useDispatch()
  const audio = useSelector<State, AudioState>(state => state.audio)
  const { songItemId, isLoading } = audio
  const { loop, bars: loopBars, beatsPerBar, request: loopRequest } = useLoop()
  const loopLoading = loopRequest?.isLoading

  const handleIncreaseBars = useCallback(() => {
    if (loopBars <= beatsPerBar) {
      const bars = loopBars * 2
      dispatch(audioActions.setLoopBeatCount(bars * beatsPerBar))
    }
  }, [beatsPerBar, loopBars, dispatch])

  const handleDecreaseBars = useCallback(() => {
    if (loopBars >= 2) {
      const bars = loopBars / 2
      dispatch(audioActions.setLoopBeatCount(bars * beatsPerBar))
    }
  }, [beatsPerBar, loopBars, dispatch])

  const onSkip = useCallback(
    (e: Record<string, unknown>, prev: boolean) => {
      if (e && e.nativeEvent && e.nativeEvent.screenX === 0 && e.nativeEvent.screenY === 0 && e.keyCode === 32) {
        return false
      }
      return dispatch(audioActions.skip(!prev))
    },
    [dispatch],
  )

  if (expanded) {
    return (
      <div className={cn(styles.expandedWrapper, className)}>
        <div className={styles.loopButtonsContainer}>
          {loop && (
            <LoopButton
              isLooping={!!loop}
              loopBars={loopBars}
              isLoading={loopLoading}
              handleLoopToggle={() => dispatch(audioActions.toggleLoop())}
              handleIncreaseBars={handleIncreaseBars}
              handleDecreaseBars={handleDecreaseBars}
              className={styles.loopButtonExpanded}
            />
          )}
          <button
            onClick={() => dispatch(audioActions.toggleLoop())}
            className={cn(styles.toggleLoopButton, loop && styles.toggleLoopButtonHidden)}
          >
            {loopLoading ? <IconLoopLoadingSmall className={styles.loopLoading} /> : <IconLoopSmall />}
          </button>
        </div>
        <button title="Previous" className={cn(styles.prevButton, styles.prevButtonExpanded)} onClick={e => onSkip(e, true)}>
          <IconPrevious />
        </button>
        <PlayButton
          className={cn(styles.playButton, styles.playButtonExpanded, playButtonClassName)}
          entity="track"
          value={{ songItemId, trackId, loop }}
          type="bordered"
          contextKey={audio.currentPlaylist}
          hideLoading
        />
        <button title="Next" className={styles.nextButton} onClick={e => onSkip(e, false)}>
          <IconNext />
        </button>
        <DownloadButton trackId={trackId} loop={loop} expanded={!expanded} className={styles.downloadButton} />
      </div>
    )
  }

  return (
    <div className={cn(styles.wrapper, className)}>
      <button
        title="Previous"
        className={styles.prevButton}
        disabled={isLoading}
        onClick={e => onSkip(e, true)}
        datatesting="player_prev_button"
      >
        <IconPrevious />
      </button>
      <PlayButton
        className={cn(styles.playButton, playButtonClassName)}
        entity="track"
        value={{ songItemId, trackId, loop: loop as Loop | undefined }}
        type="bordered"
        contextKey={audio.currentPlaylist}
        hideLoading
      />
      <button
        title="Next"
        disabled={isLoading}
        className={styles.nextButton}
        onClick={e => onSkip(e, false)}
        datatesting="player_next_button"
      >
        <IconNext />
      </button>
      <LoopButton
        isLooping={!!loop}
        loopBars={loopBars}
        isLoading={loopLoading}
        handleLoopToggle={() => dispatch(audioActions.toggleLoop())}
        handleIncreaseBars={handleIncreaseBars}
        handleDecreaseBars={handleDecreaseBars}
      />
    </div>
  )
}

export default Controls
